// Notification Service
// Handles browser notifications and toast notifications
// Sound playback is handled exclusively by audioService via rules

import toast from 'react-hot-toast';
import i18n from '../i18n';

interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  onClick?: () => void;
}

// Persistent alert for alert queue (used by AlertBar)
export interface PersistentAlert {
  id: string;
  incidentId: string;
  priority: 'p1' | 'p2';
  title: string;
  message: string;
  createdAt: string;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
}

class NotificationService {
  private permissionGranted = false;

  constructor() {
    this.checkPermission();
  }

  // Acknowledge alert (no-op for sound — alerts are visual only now)
  acknowledgeAlert(_alertId: string): void {
    // Sound is driven by rules only; nothing to stop here
  }

  private checkPermission() {
    if ('Notification' in window) {
      this.permissionGranted = Notification.permission === 'granted';
    }
  }

  async requestPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.permissionGranted = true;
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permissionGranted = permission === 'granted';
      return this.permissionGranted;
    }

    return false;
  }

  showBrowserNotification(options: NotificationOptions): void {
    if (!this.permissionGranted || document.hasFocus()) {
      return; // Don't show browser notification if page is focused
    }

    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/icon-192.png',
      tag: options.tag,
      silent: true, // Sound is driven by play_sound rule actions, not browser notifications
    });

    if (options.onClick) {
      notification.onclick = () => {
        window.focus();
        options.onClick?.();
        notification.close();
      };
    }

    // Auto-close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  }

  showToast(options: NotificationOptions): void {
    toast.custom(
      (t) => (
        <div
          className={`max-w-md w-full pointer-events-auto cursor-pointer overflow-hidden rounded-xl shadow-2xl transform transition-all duration-500 ease-out ${
            t.visible
              ? 'translate-x-0 opacity-100 scale-100'
              : 'translate-x-full opacity-0 scale-95'
          }`}
          style={{
            background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(59, 130, 246, 0.15)',
          }}
          onClick={() => {
            toast.dismiss(t.id);
            options.onClick?.();
          }}
        >
          {/* Top accent bar with gradient */}
          <div
            className="h-1 w-full"
            style={{
              background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)',
            }}
          />

          <div className="p-4">
            <div className="flex items-start gap-4">
              {/* Animated avatar */}
              <div className="relative flex-shrink-0">
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  }}
                >
                  {options.title.charAt(0).toUpperCase()}
                </div>
                {/* Pulse ring effect */}
                <div
                  className="absolute inset-0 rounded-full animate-ping opacity-30"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    animationDuration: '2s',
                  }}
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-white truncate">
                  {options.title}
                </p>
                <p className="mt-1 text-sm text-gray-300 line-clamp-2 leading-relaxed">
                  {options.body}
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  {i18n.t('common:justNow', { defaultValue: 'Just now' })}
                </p>
              </div>

              {/* Close button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toast.dismiss(t.id);
                }}
                className="flex-shrink-0 p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Bottom action hint */}
          <div className="px-4 pb-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{i18n.t('misc:clickToOpenConversation', { defaultValue: 'Click to open conversation' })}</span>
              <span className="text-primary-400 font-medium">{i18n.t('misc:viewArrow', { defaultValue: 'View →' })}</span>
            </div>
          </div>
        </div>
      ),
      {
        duration: 6000,
        position: 'top-right',
      }
    );
  }

  notifyNewMessage(
    senderName: string,
    messageContent: string,
    conversationId: string,
    onNavigate?: (conversationId: string) => void
  ): void {
    const options: NotificationOptions = {
      title: senderName,
      body: messageContent || i18n.t('misc:notif.sentAMessage', { defaultValue: 'Sent a message' }),
      tag: `chat-${conversationId}`,
      onClick: () => onNavigate?.(conversationId),
    };

    // Show browser notification if page not focused
    this.showBrowserNotification(options);

    // Always show toast notification
    this.showToast(options);
  }

  notifyNewIncident(incident: {
    id: string;
    title: string;
    priority: string;
    category?: string;
    deviceName?: string;
    location?: string;
    status?: string;
    source?: string;
    assignee?: string;
    timestamp?: string;
  }, options: {
    showToast: boolean;
    showBrowserNotification: boolean;
    onNavigate?: () => void;
    position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
    durationOverride?: number;
    fields?: {
      showPriority: boolean;
      showCategory: boolean;
      showDevice: boolean;
      showLocation: boolean;
      showStatus: boolean;
      showTime: boolean;
      showSource: boolean;
      showAssignee: boolean;
    };
  }): void {
    const priorityEmoji: Record<string, string> = {
      p1: '\uD83D\uDD34', p2: '\uD83D\uDFE0', p3: '\uD83D\uDFE1', p4: '\uD83D\uDD35', p5: '\u26AA',
    };
    const priorityLabel: Record<string, string> = {
      p1: i18n.t('misc:notif.priority.p1', { defaultValue: 'Critical' }),
      p2: i18n.t('misc:notif.priority.p2', { defaultValue: 'High' }),
      p3: i18n.t('misc:notif.priority.p3', { defaultValue: 'Medium' }),
      p4: i18n.t('misc:notif.priority.p4', { defaultValue: 'Low' }),
      p5: i18n.t('misc:notif.priority.p5', { defaultValue: 'Info' }),
    };
    const emoji = priorityEmoji[incident.priority] || '\u26AA';
    const pLabel = priorityLabel[incident.priority] || incident.priority;

    // Build body from configured fields
    const fields = options.fields;
    const bodyParts: string[] = [];
    if (fields) {
      if (fields.showCategory && incident.category) bodyParts.push(i18n.t('misc:notif.field.category', { defaultValue: 'Category: {{value}}', value: incident.category }));
      if (fields.showDevice && incident.deviceName) bodyParts.push(i18n.t('misc:notif.field.device', { defaultValue: 'Device: {{value}}', value: incident.deviceName }));
      if (fields.showLocation && incident.location) {
        const loc = incident.location.length > 80 ? incident.location.substring(0, 77) + '...' : incident.location;
        bodyParts.push(i18n.t('misc:notif.field.location', { defaultValue: 'Location: {{value}}', value: loc }));
      }
      if (fields.showStatus && incident.status) bodyParts.push(i18n.t('misc:notif.field.status', { defaultValue: 'Status: {{value}}', value: incident.status }));
      if (fields.showTime && incident.timestamp) bodyParts.push(i18n.t('misc:notif.field.time', { defaultValue: 'Time: {{value}}', value: incident.timestamp }));
      if (fields.showSource && incident.source) bodyParts.push(i18n.t('misc:notif.field.source', { defaultValue: 'Source: {{value}}', value: incident.source }));
      if (fields.showAssignee && incident.assignee) bodyParts.push(i18n.t('misc:notif.field.assigned', { defaultValue: 'Assigned: {{value}}', value: incident.assignee }));
    } else {
      // Fallback: show all available fields
      if (incident.category) bodyParts.push(i18n.t('misc:notif.field.category', { defaultValue: 'Category: {{value}}', value: incident.category }));
      if (incident.deviceName) bodyParts.push(i18n.t('misc:notif.field.device', { defaultValue: 'Device: {{value}}', value: incident.deviceName }));
      if (incident.status) bodyParts.push(i18n.t('misc:notif.field.status', { defaultValue: 'Status: {{value}}', value: incident.status }));
    }
    const body = bodyParts.join(' | ');

    const priorityColors: Record<string, string> = {
      p1: '#ef4444', p2: '#f97316', p3: '#eab308', p4: '#3b82f6', p5: '#6b7280',
    };
    const accentColor = priorityColors[incident.priority] || '#3b82f6';
    const defaultDuration = ['p1', 'p2'].includes(incident.priority) ? 8000 : 6000;
    const duration = options.durationOverride && options.durationOverride > 0 ? options.durationOverride : defaultDuration;
    const position = options.position || 'top-right';
    const showPriorityBadge = fields ? fields.showPriority : true;

    if (options.showToast) {
      toast.custom(
        (t) => (
          <div
            className={`max-w-md w-full pointer-events-auto cursor-pointer overflow-hidden rounded-xl shadow-2xl transform transition-all duration-500 ease-out ${
              t.visible
                ? 'translate-x-0 opacity-100 scale-100'
                : 'translate-x-full opacity-0 scale-95'
            }`}
            style={{
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              border: `1px solid ${accentColor}40`,
              boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px ${accentColor}20`,
            }}
            onClick={() => {
              toast.dismiss(t.id);
              options.onNavigate?.();
            }}
          >
            <div className="h-1 w-full" style={{ background: accentColor }} />
            <div className="p-4">
              <div className="flex items-start gap-3">
                {showPriorityBadge && (
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ background: accentColor }}
                  >
                    {priorityLabel[incident.priority] || incident.priority}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{incident.title}</p>
                  {body && <p className="mt-0.5 text-xs text-gray-400">{body}</p>}
                  <p className="mt-1.5 text-xs text-gray-500">{i18n.t('incidents:clickToView', { defaultValue: 'Click to view incident' })}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toast.dismiss(t.id); }}
                  className="flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-white hover:bg-gray-700/50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ),
        { duration, position }
      );
    }

    if (options.showBrowserNotification) {
      this.showBrowserNotification({
        title: i18n.t('misc:notif.incidentTitle', { defaultValue: '[{{priority}}] Incident: {{title}}', priority: pLabel, title: incident.title }),
        body,
        tag: `incident-${incident.id}`,
        onClick: () => {
          window.focus();
          options.onNavigate?.();
        },
      });
    }
  }
}

export const notificationService = new NotificationService();
